import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useForm } from "@tanstack/react-form";

import { addProductBrandSchema } from "@/zod-schemas/products/add-product-brand-schema";

import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RequiredInput } from "../common/required-input";
import { Separator } from "@/components/ui/separator";

import { toast } from "sonner";
import { useState } from "react";

import { useUpdateProductBrand } from "@/hooks/products/use-product-brands";
import type { ProductBrand } from "@/types/products/api-types/product-brand-types";
import { getProductBrandFormValues } from "@/default-form-values/products/default-product-brand-form-values";

interface EditBrandDialogProps {
  brand: ProductBrand;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditBrandDialog({
  brand,
  open,
  onOpenChange,
}: EditBrandDialogProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const updateBrand = useUpdateProductBrand();

  const form = useForm({
    defaultValues: getProductBrandFormValues(brand),
    validators: {
      onSubmit: addProductBrandSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const validated = addProductBrandSchema.parse(value);
        await updateBrand.mutateAsync({
          id: brand.id,
          data: {
            name: validated.name,
          },
        });
        toast.success(`Brand "${validated.name}" updated successfully`);
        onOpenChange(false);
      } catch (error: any) {
        const statusCode = error?.response?.status;

        if (statusCode === 409) {
          setServerError(
            `A brand with the name "${value.name}" already exists`
          );
        } else {
          toast.error(`Failed to update brand "${value.name}"`);
          onOpenChange(false);
        }
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) {
          form.reset();
          setServerError(null);
        }
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Brand</DialogTitle>
        </DialogHeader>
        <Separator />

        <form
          className="mb-4"
          id="edit-brand-form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field
              name="name"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Name
                      <RequiredInput />
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setServerError(null);
                      }}
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                    {serverError && <FieldError>{serverError}</FieldError>}
                  </Field>
                );
              }}
            />
          </FieldGroup>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" form="edit-brand-form" className="w-24">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
